/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { Link } from "react-router-dom";
import {
  Text,
  theme,
  FlexContainer,
  ExtendedTooltip,
  SVGIcon,
} from "@nordcloud/gnui";
import { CostCell } from "~/components";
import { TableExpanderTemplate } from "~/components/Tables/Columns/Expander";
import { useCurrency } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { trimText, formatMoneyWithFallback, formatMoney } from "~/tools";
import { Row, SpendTreeItem } from "../types";
import {
  getCostFieldValue,
  getSumOfBudgets,
  getSumOfCosts,
  getSumOfForecasts,
  getSumOfPreviousCosts,
} from "../utils";

type Data = {
  row: Row;
  data: SpendTreeItem[];
};

const MAX_NAME_CHARS = 50;

export const OrganizationUnitChildTableColumns = (structureId: string) => {
  return [
    {
      Header: "Organizational Unit Name",
      accessor: "name",
      disableSortBy: true,
      Cell: ({ row }: Data) => {
        const unitType = row.original.unitType;
        const contactPerson = row.original.contactPerson;
        const showAdditionalInfo =
          isNotEmptyValue(unitType) || isNotEmptyValue(contactPerson);

        return (
          <FlexContainer data-testid="org-unit-name">
            <TableExpanderTemplate raw row={row} />
            <FlexContainer
              direction="column"
              alignItems="flex-start"
              css={{
                flex: 1,
              }}
            >
              <Link
                to={`${ROUTES.businessContexts.index}/${structureId}/${row?.original?.nid}`}
              >
                {trimText(row.original.name, MAX_NAME_CHARS)}
              </Link>
              {showAdditionalInfo && (
                <Text color={theme.color.text.text02} size="sm" mb={0}>
                  {row.original.unitType}
                  <span
                    css={{
                      margin: `0 ${theme.spacing.spacing02}`,
                    }}
                  >
                    |
                  </span>
                  {row.original.contactPerson}
                </Text>
              )}
            </FlexContainer>
          </FlexContainer>
        );
      },
    },
    {
      Header: "Environments",
      accessor: "environments",
      width: "2rem",
      disableSortBy: true,
      Cell: ({ row }: Data) => (
        <Text align="right" tag="div" size="md" mb={0}>
          {row.isExpanded
            ? row.original.environments
            : row.original.accumulatedEnvironments}
        </Text>
      ),
      Footer: () => (
        <Text align="right" tag="div" size="md" mb={0} weight="medium">
          Total:
        </Text>
      ),
    },
    {
      Header: "Current Month",
      accessor: "cost",
      disableSortBy: true,
      hasLeftBorder: true,
      hasRightBorder: true,
      width: "8rem",
      Cell: ({ row }: Data) => (
        <CostCell value={getCostFieldValue("cost", row)} />
      ),
      Footer: ({ data }: Data) => <CostCell value={getSumOfCosts(data)} />,
    },
    {
      Header: "Monthly Forecast",
      accessor: "forecast",
      disableSortBy: true,
      hasLeftBorder: true,
      hasRightBorder: true,
      width: "10rem",
      Cell: ({ row }: Data) => (
        <CostCell
          moneyFormatter={formatMoneyWithFallback}
          value={getCostFieldValue("forecast", row)}
        />
      ),
      Footer: ({ data }: Data) => <CostCell value={getSumOfForecasts(data)} />,
    },
    {
      Header: "Previous Month",
      accessor: "prevCost",
      disableSortBy: true,
      hasLeftBorder: true,
      hasRightBorder: false,
      width: "8rem",
      Cell: ({ row }: Data) => (
        <CostCell value={getCostFieldValue("prevCost", row)} />
      ),
      Footer: ({ data }: Data) => (
        <CostCell value={getSumOfPreviousCosts(data)} />
      ),
    },
    {
      Header: "Monthly Budget",
      accessor: "monthlyBudget",
      disableSortBy: true,
      hasLeftBorder: true,
      hasRightBorder: false,
      width: "8rem",
      Cell: ({ row }: Data) => <MonthlyBudgetCell row={row} />,
      Footer: ({ data }: Data) => <CostCell value={getSumOfBudgets(data)} />,
    },
  ];
};

type Props = {
  row: Data["row"];
};

function MonthlyBudgetCell({ row }: Props) {
  const { currency } = useCurrency();
  const monthlyBudget = row.original.monthlyBudget ?? 0;
  const childrenBudget = row.original.childrenBudget;
  const showBudgetWarning = monthlyBudget < childrenBudget;

  const formattedWarningCaption = (
    <span>
      The total environmental and sub-budgets are greater than the
      Organizational Unit budget. <br /> TOTAL{" "}
      {formatMoney(childrenBudget, currency, false)}{" "}
    </span>
  );

  // TODO - tooltip doesn't work, because of negative margins. Will be fixed along with rewrite of Tooltip (KLAR-4378)
  return (
    <FlexContainer>
      <CostCell
        value={getCostFieldValue("monthlyBudget", row)}
        moneyFormatter={formatMoneyWithFallback}
      />
      <When condition={showBudgetWarning}>
        <div
          css={{
            marginLeft: `-${theme.spacing.spacing04}`,
            marginTop: `-${theme.spacing.spacing01}`,
          }}
        >
          <ExtendedTooltip caption={formattedWarningCaption}>
            <SVGIcon name="statusWarning" size="sm" />
          </ExtendedTooltip>
        </div>
      </When>
    </FlexContainer>
  );
}

function isNotEmptyValue(value: string) {
  return value !== "-";
}
